import { Container, ObservablePoint, Point } from 'pixi.js';
import {
  IAnimateOptions,
  IFollowOptions,
  ISnapOptions,
  ISnapZoomOptions,
  IViewportOptions,
  IWheelOptions,
  Viewport
} from 'pixi-viewport';

import { GameManager } from '../game';

export enum FitType {
  none,
  world,
  width,
  height
}

const DEFAULT_VIEWPORT_SETTINGS = {
  screenWidth: window.innerWidth,
  screenHeight: window.innerHeight,
  passiveWheel: false
};

export class CameraManager {
  private static _viewport: Viewport;
  private static _followTarget: Container;
  private static _cachedFollowOptions?: Partial<IFollowOptions>;
  private static _cachedOptions?: Partial<IViewportOptions>;
  private static _zoom = 1;
  private static _fitType: FitType = FitType.none;

  public static get camera(): Viewport {
    if (!CameraManager._viewport) {
      CameraManager.initialize();
    }

    return CameraManager._viewport;
  }

  public static set fit(fitType: FitType) {
    this._fitType = fitType;
    GameManager.resize();
  }

  public static set zoom(scale: number) {
    this._zoom = scale;
    GameManager.resize();
  }

  public static get zoom(): number {
    return CameraManager._zoom;
  }

  public static get scale(): ObservablePoint {
    return CameraManager._viewport.scale;
  }

  public static get left(): number {
    return CameraManager._viewport.left;
  }

  public static get right(): number {
    return CameraManager._viewport.right;
  }

  public static get top(): number {
    return CameraManager._viewport.top;
  }

  public static get bottom(): number {
    return CameraManager._viewport.bottom;
  }

  /**
   * Checks if a given point is visible within the camera's visible bounds.
   *
   * @param point - The point to check for visibility.
   * @returns A boolean indicating whether the point is visible or not.
   */
  public static isPointVisible(point: Point): boolean {
    return CameraManager.camera.getVisibleBounds().contains(point.x, point.y);
  }

  /**
   * Initializes the Camera Manager
   *
   * @param width - Width of the viewport in pixels
   * @param height - Height of the viewport in pixels
   */
  public static initialize(options?: Partial<IViewportOptions>): void {
    this._cachedOptions = options;
    this._cachedFollowOptions = undefined;
    this.createViewport();
  }

  /**
   * Resets the camera
   */
  public static reset(): void {
    this._zoom = 1;
    this._fitType = FitType.none;

    GameManager.app.stage.removeChild(CameraManager._viewport);
    CameraManager._viewport.destroy();

    this.createViewport();
  }

  private static createViewport() {
    CameraManager._viewport = new Viewport({
      ...DEFAULT_VIEWPORT_SETTINGS,
      events: GameManager.app.renderer.events,
      ...this._cachedOptions
    });

    GameManager.app.stage.addChild(CameraManager._viewport);

    this.resize();
  }

  /**
   * The camera will follow the target with the given options.
   * If a target is currently being followed, it will be replaced with the new target.
   *
   * @param target - The target to follow
   * @param options - Adjust follow camera settings
   */
  public static followTarget(target: Container, options?: IFollowOptions): void {
    if (this._followTarget) {
      CameraManager.stopFollowing();
    }

    if (options) {
      CameraManager._cachedFollowOptions = options;
    }
    CameraManager._viewport.follow(target, CameraManager._cachedFollowOptions);
    this._followTarget = target;
  }

  /**
   * Stops the camera from following it's current target
   */
  public static stopFollowing(): void {
    if (this._followTarget) {
      CameraManager._viewport.plugins.remove('follow');
    }
  }

  /**
   * Animate the position and/or scale of the viewport.
   * Useful for panning the camera or a gradual zoom.
   *
   * @param options - Options to animate the camera
   */
  public static animate(options: IAnimateOptions): void {
    CameraManager._viewport.animate(options);
  }

  /**
   * Snaps the camera to the given world co-ordinates.
   *
   * @param x - x position in world co-ordinates
   * @param y - y position in world co-ordinates
   * @param options - Adjust snapping options
   */
  public static snapTo(x: number, y: number, options?: ISnapOptions): void {
    CameraManager._viewport.snap(x, y, options);
  }

  /**
   * Snaps the camera zoom to the given options.
   *
   * @param options - Zoom options
   */
  public static snapZoom(options?: ISnapZoomOptions): void {
    CameraManager._viewport.snapZoom(options);
  }

  /**
 * Gradually zooms out the camera to the specified scale and centers it at the new position.
 *
 * @param targetScale - The target scale to zoom out to.
 * @param targetPosition - The target position to center the camera on.
 * @param time - The duration of the animation in milliseconds (optional, defaults to 1000ms).
 * @param completeCallback - A callback function to be called upon completion of the animation (optional).
 */
  public static animateZoomTo(x: number, y: number, time = 1000, targetScale?: number, completeCallback?: () => void): void {
    const options: IAnimateOptions = {
      time,
      position: { x, y },
      callbackOnComplete: completeCallback,
      ...(targetScale && { scale: targetScale })
    };
    CameraManager.animate(options);
  }

  /**
   * Toggles zooming via mouse wheel
   *
   * @param options - wheel options
   */
  public static toggleMouseWheelZoom(options?: IWheelOptions) {
    if (!CameraManager._viewport.plugins.get('wheel')) {
      CameraManager._viewport.wheel(options);
    }
    else {
      CameraManager._viewport.plugins.remove('wheel');
    }
  }

  public static clampBounds(left: number, right: number, top: number, bottom: number): void {
    if (CameraManager.camera.left < left) {
      CameraManager.camera.left = left;
    }
    else if (CameraManager.camera.right > right) {
      CameraManager.camera.right = right;
    }

    if (CameraManager.camera.top < top) {
      CameraManager.camera.top = top;
    }
    else if (CameraManager.camera.bottom > bottom) {
      CameraManager.camera.bottom = bottom;
    }
  }

  /**
   * The resize event callback for the main camera.
   */
  public static resize(): void {
    CameraManager.camera.resize(GameManager.screenWidth, GameManager.screenHeight);
    switch (this._fitType) {
      case FitType.world:
      {
        CameraManager.camera.fitWorld();

        // Calculate the scale needed to fit the world dimensions to the screen
        const scaleX = GameManager.screenWidth / CameraManager.camera.worldWidth;
        const scaleY = GameManager.screenHeight / CameraManager.camera.worldHeight;
        const scale = Math.min(scaleX, scaleY); // Use the smaller scale to maintain aspect ratio

        CameraManager.camera.scale.set(scale);
        CameraManager.camera.moveCenter(CameraManager.camera.worldWidth / 2, CameraManager.camera.worldHeight / 2);
        break;
      }
      case FitType.width:
        CameraManager.camera.fitWidth(this._cachedOptions?.worldWidth || 1920 / this._zoom);
        break;
      case FitType.height:
        CameraManager.camera.fitHeight(this._cachedOptions?.worldHeight || 1080 / this._zoom);
        break;
    }
  }

  /**
   * Destroys the CameraManager and cleans up the viewport.
   */
  public static destroy(): void {
    if (this._viewport) {
      // Remove from stage if it exists
      if (GameManager.app?.stage && this._viewport.parent) {
        GameManager.app.stage.removeChild(this._viewport);
      }

      // Stop following any target
      this.stopFollowing();

      // Destroy the viewport
      this._viewport.destroy();
      this._viewport = undefined as never;
    }

    // Reset state
    this._followTarget = undefined as never;
    this._cachedFollowOptions = undefined;
    this._cachedOptions = undefined;
    this._zoom = 1;
    this._fitType = FitType.none;
  }
}
